<template>
  <div class="mc-fuel-insight">
    <div class="mc-fuel-insight__content-heading mb-3">
      <button
        v-if="hasContents"
        type="button"
        class="mc-fuel-insight__back"
        @click="backToHistories"
      >
        <i class="mc-fa icon-outline_arrow-left"></i>
      </button>
      <h5 class="mc-fuel-insight__content-title">
        {{ $t('global.title.getInsight') }}
        <i class="mc-fa icon-outline_ai"></i>
      </h5>
      <p class="mc-fuel-insight__content-subtitle">
        {{ $t('dashboard.text.getInsightDescFuelDashboard') }}
      </p>
    </div>
    <div v-if="hasContents" class="mc-fuel-insight__content-header">
      <div class="mc-fuel-insight__content-heading">
        <div class="mc-fuel-insight-filter">
          <div class="mc-fuel-insight-filter__item">
            <i class="mc-fa icon-outline_calendar"></i>
            <span>{{ historyPeriodLabel }}</span>
          </div>
          <div class="mc-fuel-insight-filter__divider"></div>
          <div class="mc-fuel-insight-filter__item">
            <i class="mc-fa icon-outline_car-group"></i>
            <span>{{ historyFilterLabel }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="mc-fuel-insight__loading">
      <img
        class="mc-fuel-insight__loading-illustration"
        src="@/assets/images/dashboard/fuel-insight-loading.svg"
        alt=""
        aria-hidden="true"
      />
      <span class="mc-fuel-insight__loading-text">
        {{ $t('dashboard.text.fuelInsightLoading') }}
      </span>
    </div>

    <template v-else-if="!hasContents">
      <div
        class="mc-fuel-insight__start"
        :class="{ 'mc-fuel-insight__start--compact': hasHistories }"
      >
        <img
          class="mc-fuel-insight__start-illustration"
          src="@/assets/images/dashboard/fuel-insight-start.svg"
          alt=""
          aria-hidden="true"
        />

        <h6 class="mc-fuel-insight__start-title">
          {{ $t('dashboard.text.fuelInsightStartTitle') }}
        </h6>
        <p class="mc-fuel-insight__start-description">
          {{ $t('dashboard.text.fuelInsightStartDescription') }}
        </p>
        <b-button
          variant="primary"
          size="sm"
          class="mc-fuel-insight__start-button"
          :disabled="!canRequestInsight"
          @click="requestLllmMessage"
        >
          {{ $t('dashboard.label.analyzeFuelData') }}
        </b-button>
        <span class="mc-fuel-insight__quota">
          {{
            $t('dashboard.text.fuelInsightQuota', {
              remaining: remainingDailyAttempt,
              total: getMaxAttemptDaily,
            })
          }}
        </span>
      </div>

      <div v-if="hasHistories" class="mc-fuel-insight__history">
        <div class="mc-fuel-insight__history-header">
          <span>{{ $t('dashboard.label.fuelInsightHistory') }}</span>
        </div>

        <div class="mc-fuel-insight__history-list">
          <button
            v-for="(history, historyIndex) in historyData.data"
            :key="`${history.createdOn}-${historyIndex}`"
            type="button"
            class="mc-fuel-insight__history-card"
            @click="showHistory(history)"
          >
            <span class="mc-fuel-insight__history-content">
              <span class="mc-fuel-insight__history-period">
                <i class="mc-fa icon-outline_calendar"></i>
                {{ getHistoryPeriodLabel(history.filter) }}
              </span>
              <div class="mc-fuel-insight-filter__divider"></div>
              <span class="mc-fuel-insight__history-group">
                <i class="mc-fa icon-outline_car-group"></i>
                {{ getHistoryFilterLabel(history.filter) }}
              </span>
            </span>
            <span class="mc-fuel-insight__history-action">
              {{ $t('dashboard.label.viewFuelInsightHistory') }}
            </span>
          </button>

          <div
            v-if="hasNextHistoryPage"
            v-observe-visibility="handleHistoryObserved"
            class="mc-fuel-insight__history-sentinel"
          >
            <span v-if="isHistoryLoading" class="mc-fuel-insight__history-loading">
              <span class="mc-fuel-insight__history-loading-dot"></span>
              <span class="mc-fuel-insight__history-loading-dot"></span>
              <span class="mc-fuel-insight__history-loading-dot"></span>
            </span>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="hasContents">
      <div
        v-for="(section, sectionIndex) in contents.sections"
        :key="`${section.title}-${sectionIndex}`"
        class="mc-fuel-insight__section"
      >
        <h6 class="mc-fuel-insight__title" v-html="section.title" />

        <div class="mc-fuel-insight__summary">
          <p
            v-for="(bullet, bulletIndex) in section.bullets"
            :key="bulletIndex"
            class="mc-fuel-insight__summary-point"
          >
            <span class="mc-fuel-insight__summary-icon" aria-hidden="true"></span>
            <span v-html="bullet" />
          </p>
        </div>

        <template v-if="section.expanded.length">
          <div class="mc-fuel-insight__answers">
            <button
              type="button"
              class="mc-fuel-insight__answer"
              :class="{ 'mc-fuel-insight__answer--open': isOpen(sectionIndex) }"
              @click="toggleAnswer(sectionIndex)"
            >
              <span class="mc-fuel-insight__answer-heading">
                <span>{{ $t('global.object.quickSummary') }}</span>
                <i
                  class="mc-icon"
                  :class="
                    isOpen(sectionIndex) ? 'ic`on-outline_chevron-up' : 'icon-outline_chevron-down'
                  "
                ></i>
              </span>
              <span
                v-if="isOpen(sectionIndex) && section.expanded"
                class="mc-fuel-insight__answer-content"
                v-html="section.expanded"
              />
            </button>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { GET_LLM_REQUEST_HISTORY, REQUEST_LLM_MESSAGE } from '@/services/api/dashboard/dashboard'
import moment from 'moment'
import { GET_VEHICLE_GROUPS } from '@/services/api/master/fleet'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  name: 'FuelInsight',
  apollo: {
    vgroups: {
      query: GET_VEHICLE_GROUPS,
      result({ data, loading }) {
        if (loading) return
        let vgroups = { ...data.vgroups }
        this.vgroups = vgroups
      },
    },
  },
  data() {
    return {
      type: '',
      typeOld: '',
      filter: null,
      contents: {},
      isLoading: false,
      isHistoryLoading: false,
      historyPage: 1,
      historyLimit: 10,
      openAnswers: {},
      histories: {
        dailyAttempt: 0,
        maxAttempt: 0,
        data: [],
        pagination: {},
      },
      vgroupId: [],
      vehicleTypeId: [],
      period: [],
      vgroups: {
        vgroups: [],
      },
      selectedFilter: {
        currentPeriod: {
          start: '',
          end: '',
        },
        previousPeriod: {
          start: '',
          end: '',
        },
        vehicleGroupId: [],
        vehicleTypeId: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    hasContents() {
      return this.contents.sections && this.contents.sections.length > 0
    },
    hasHistories() {
      return Array.isArray(this.histories.data) && this.histories.data.length > 0
    },
    canRequestInsight() {
      return Boolean(this.type.length > 0 && this.filter && this.remainingDailyAttempt > 0)
    },
    remainingDailyAttempt() {
      return this.histories.dailyAttempt
    },
    getMaxAttemptDaily() {
      return this.histories.maxAttempt
    },
    historyData() {
      return this.histories
    },
    historyPagination() {
      return this.histories.pagination || {}
    },
    hasNextHistoryPage() {
      const total = Number(this.historyPagination.total || 0)
      const currentCount = Array.isArray(this.histories.data) ? this.histories.data.length : 0

      return total > currentCount
    },
    historyPeriodLabel() {
      const start = this.selectedFilter.currentPeriod && this.selectedFilter.currentPeriod.start
      const end = this.selectedFilter.currentPeriod && this.selectedFilter.currentPeriod.end

      if (!start || !end) {
        return '-'
      }

      return `${moment(start).format("D MMM 'YY")} - ${moment(end).format("D MMM 'YY")}`
    },
    historyFilterLabel() {
      const vgroupId = this.selectedFilter.vehicleGroupId || []
      const vtypeId = this.selectedFilter.vehicleTypeId || []
      let vehicleGroupLabel = this.getVehicleGroupsLabel(vgroupId)
      let vehicleTypeLabel = this.$t('global.text.allVehicleType')
      if (vtypeId.length) {
        vehicleTypeLabel = `${vtypeId.length} ${this.$t('dashboard.table.vehicleType')}`
      }
      return [vehicleGroupLabel, vehicleTypeLabel].join(', ')
    },
  },
  methods: {
    getVehicleGroupsLabel(vgroupId) {
      if (!vgroupId || !vgroupId.length) {
        return this.$t('global.text.allVehicleGroup')
      }

      const vgroupLabels = this.vgroups.vgroups
        .filter((vg) => vgroupId.includes(vg.id))
        .map((vg) => vg.name)

      if (vgroupLabels.length > 5) {
        return `${vgroupLabels.slice(0, 5).join(', ')} +${vgroupLabels.length - 5}`
      }

      return vgroupLabels.length ? vgroupLabels.join(', ') : this.$t('global.text.allVehicleGroup')
    },
    async initData(filter) {
      this.vgroupId = filter.vgroupId
      this.vehicleTypeId = filter.vehicleTypeId
      this.period = [filter.startDate, filter.endDate]
      const startDate = moment(filter.startDate)
      const endDate = moment(filter.endDate)
      const dateDiff = endDate.diff(startDate, 'day')
      const prevEndDate = startDate.clone().subtract(1, 'day')
      const prevStartDate = prevEndDate.clone().subtract(dateDiff, 'day')

      this.type = 'dashboardFuel'
      this.filter = {
        currentPeriod: {
          start: moment(this.period[0] + ' 00:00:00.000').toISOString(),
          end: moment(this.period[1] + ' 23:59:59.000').toISOString(),
        },
        previousPeriod: {
          start: moment(prevStartDate.format('YYYY-MM-DD') + ' 00:00:00.000').toISOString(),
          end: moment(prevEndDate.format('YYYY-MM-DD') + ' 23:59:59.000').toISOString(),
        },
        vehicleGroupId: filter.vgroupId && filter.vgroupId.length ? filter.vgroupId : undefined,
        vehicleTypeId:
          filter.vehicleTypeId && filter.vehicleTypeId.length ? filter.vehicleTypeId : undefined,
      }
      if (this.type != this.typeOld) await this.requestLlmMessageHistory(true)
      this.typeOld = this.type
    },
    async requestLlmMessageHistory(reset) {
      if (!this.type || this.isHistoryLoading) return

      const page = reset ? 1 : this.historyPage + 1
      this.isHistoryLoading = true

      try {
        const response = await this.$apollo.query({
          query: GET_LLM_REQUEST_HISTORY,
          variables: {
            filter: {
              pagination: {
                page,
                show: this.historyLimit,
              },
              type: this.type,
            },
          },
          fetchPolicy: 'no-cache',
        })
        const data = response.data
        const histories = data.getRequestLlmMessageHistory.getRequestLlmMessageHistory

        this.historyPage = histories.pagination?.page || page
        this.histories = {
          ...histories,
          data: reset ? histories.data || [] : [...this.histories.data, ...(histories.data || [])],
        }
      } finally {
        this.isHistoryLoading = false
      }
    },
    async requestLllmMessage() {
      if (this.canRequestInsight) {
        this.isLoading = true
        const response = await this.$apollo.mutate({
          mutation: REQUEST_LLM_MESSAGE,
          variables: {
            input: {
              type: this.type,
              filter: {
                ...this.filter,
                disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
              },
            },
          },
        })
        const data = response.data
        this.contents = this.parseContents(data.requestLlmMessage.requestLlmMessage.content)
        this.selectedFilter = this.filter
        this.isLoading = false
        await this.requestLlmMessageHistory(true)
      }
    },
    handleHistoryObserved(isVisible) {
      if (!isVisible || !this.hasNextHistoryPage) return

      this.requestLlmMessageHistory()
    },

    answerKey(sectionindex) {
      return `expanded-${sectionindex}`
    },
    parseContents(contents) {
      const stripMarkdownJsonFence = (value) => {
        const text = value.trim()
        const match = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)

        return match ? match[1].trim() : text
      }

      let parsed = contents

      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed)
      }

      if (Array.isArray(parsed) && parsed[0]?.text) {
        parsed = parsed[0].text
      }

      if (typeof parsed === 'string') {
        parsed = stripMarkdownJsonFence(parsed)
        parsed = JSON.parse(parsed)
      }

      return parsed
    },
    parseFilter(filter) {
      try {
        return typeof filter === 'string' ? JSON.parse(filter) : filter || {}
      } catch (error) {
        return {}
      }
    },
    showHistory(history) {
      this.contents = this.parseContents(history.contents)
      this.filter = this.parseFilter(history.filter)
      this.selectedFilter = this.filter
    },
    backToHistories() {
      this.contents = {}
      this.openAnswers = {}
    },
    getHistoryPeriodLabel(filter) {
      const parsedFilter = this.parseFilter(filter)
      const start = parsedFilter.currentPeriod && parsedFilter.currentPeriod.start
      const end = parsedFilter.currentPeriod && parsedFilter.currentPeriod.end

      if (!start || !end) {
        return '-'
      }

      return `${moment(start).format("D MMM 'YY")} - ${moment(end).format("D MMM 'YY")}`
    },
    getHistoryFilterLabel(filter) {
      const parsedFilter = this.parseFilter(filter)
      const vgroupId = parsedFilter.vehicleGroupId || []
      const vtypeId = parsedFilter.vehicleTypeId || []
      let vehicleGroupLabel = this.getVehicleGroupsLabel(vgroupId)
      let vehicleTypeLabel = this.$t('global.text.allVehicleType')
      if (vtypeId.length) {
        vehicleTypeLabel = `${vtypeId.length} ${this.$t('dashboard.table.vehicleType')}`
      }
      return [vehicleGroupLabel, vehicleTypeLabel].join(', ')
    },
    isOpen(sectionIndex) {
      return Boolean(this.openAnswers[this.answerKey(sectionIndex)])
    },
    toggleAnswer(sectionIndex) {
      const key = this.answerKey(sectionIndex)
      this.$set(this.openAnswers, key, !this.openAnswers[key])
    },
  },
}
</script>

<style scoped>
.back-button {
  font-size: 10;
}
.mc-fuel-insight {
  color: #475467;
  margin-top: 16px;
}

.mc-fuel-insight__start {
  align-items: center;
  border: 1px solid #dfe3eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  padding: 16px 12px 12px;
  text-align: center;
}

.mc-fuel-insight__start--compact {
  align-items: flex-start;
  padding: 16px 12px 12px;
  position: relative;
  text-align: left;
}

.mc-fuel-insight__start--compact .mc-fuel-insight__start-illustration {
  height: 56px;
  margin: 0;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 56px;
}

.mc-fuel-insight__start--compact .mc-fuel-insight__start-button {
  width: 100%;
}

.mc-fuel-insight__start--compact .mc-fuel-insight__quota {
  align-self: center;
}

.mc-fuel-insight__loading {
  align-items: center;
  border: 1px solid #dfe3eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 230px;
  padding: 28px 12px 16px;
  text-align: center;
}

.mc-fuel-insight__loading-illustration {
  height: 120px;
  width: 160px;
}

.mc-fuel-insight__loading-text {
  color: #667085;
  font-size: 13px;
  line-height: 18px;
}

.mc-fuel-insight__start-illustration {
  height: 120px;
  margin-bottom: 8px;
  width: 160px;
}

.mc-fuel-insight__start-title {
  color: #344054;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 4px;
}

.mc-fuel-insight__start-description {
  color: #667085;
  font-size: 11px;
  line-height: 15px;
  margin-bottom: 16px;
  max-width: 210px;
}

.mc-fuel-insight__start-button {
  font-size: 11px;
  min-width: 140px;
}

.mc-fuel-insight__quota {
  color: #667085;
  font-size: 10px;
  line-height: 14px;
  margin-top: 8px;
}

.mc-fuel-insight__history {
  margin-top: 20px;
}

.mc-fuel-insight__history-header {
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  color: #667085;
  display: flex;
  font-size: 11px;
  justify-content: space-between;
  letter-spacing: 0.02em;
  line-height: 16px;
  padding-bottom: 12px;
  text-transform: uppercase;
}

.mc-fuel-insight__history-list {
  max-height: clamp(180px, calc(100vh - 520px), 360px);
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
}

.mc-fuel-insight__history-list::-webkit-scrollbar {
  width: 6px;
}

.mc-fuel-insight__history-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.mc-fuel-insight__history-list::-webkit-scrollbar-track {
  background: transparent;
}

.mc-fuel-insight__history-delete {
  background: transparent;
  border: 0;
  color: #f04438;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}

.mc-fuel-insight__history-card {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 6px;
  color: #344054;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-top: 8px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}

.mc-fuel-insight__history-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.mc-fuel-insight__history-period,
.mc-fuel-insight__history-group {
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 6px;
  line-height: 16px;
}

.mc-fuel-insight__history-period i,
.mc-fuel-insight__history-group i {
  color: #667085;
  font-size: 14px;
}

.mc-fuel-insight__history-group {
  color: #475467;
  font-size: 12px;
  line-height: 16px;
}

.mc-fuel-insight__history-action {
  color: #0d4491;
  flex: 0 0 auto;
  font-size: 12px;
  line-height: 16px;
}

.mc-fuel-insight__history-sentinel {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 28px;
}

.mc-fuel-insight__history-loading {
  display: flex;
  gap: 4px;
}

.mc-fuel-insight__history-loading-dot {
  animation: mc-fuel-insight-history-loading 0.9s ease-in-out infinite;
  background: #98a2b3;
  border-radius: 50%;
  height: 5px;
  width: 5px;
}

.mc-fuel-insight__history-loading-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.mc-fuel-insight__history-loading-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes mc-fuel-insight-history-loading {
  0%,
  100% {
    opacity: 0.4;
    transform: translateY(0);
  }

  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

.mc-fuel-insight__content-header {
  align-items: flex-start;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  position: relative;
}

.mc-fuel-insight__back {
  position: absolute;
  float: left;
  align-items: center;
  background: transparent;
  border: 0;
  color: #344054;
  display: flex;
  flex: 0 0 24px;
  font-size: 16px;
  height: 24px;
  justify-content: center;
  padding: 0;
}

.mc-fuel-insight__content-heading {
  flex: 1;
  padding-right: 24px;
  text-align: center;
}

.mc-fuel-insight__content-title {
  color: #344054;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 4px;
}

.mc-fuel-insight__content-subtitle {
  color: #667085;
  font-size: 11px;
  line-height: 15px;
  margin-bottom: 0;
}

.mc-fuel-insight-filter {
  border: 1px solid #dfe3eb;
  border-radius: 4px;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
  margin-top: 16px;
  padding: 8px 16px;
  text-align: left;
}

.mc-fuel-insight-filter__item {
  align-items: center;
  display: flex;
  gap: 8px;
  min-height: 22px;
}

.mc-fuel-insight-filter__item i {
  color: #344054;
  font-size: 16px;
}

.mc-fuel-insight-filter__divider {
  border-top: 1px solid #e5e7eb;
  margin: 3px 0;
  margin-left: 20px;
  margin-right: 5px;
}

.mc-fuel-insight__section {
  border-bottom: 1px solid #e5e7eb;
  padding: 0 0 16px;
}

.mc-fuel-insight__section + .mc-fuel-insight__section {
  padding-top: 16px;
}

.mc-fuel-insight__title {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 8px;
}

.mc-fuel-insight__summary {
  font-size: 12px;
  line-height: 16px;
}

.mc-fuel-insight__summary-point {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  white-space: pre-line;
}

.mc-fuel-insight__summary-icon {
  background: #2076da;
  border-radius: 50%;
  flex: 0 0 6px;
  height: 6px;
  margin-top: 5px;
  width: 6px;
}

.mc-fuel-insight__quick-answer {
  color: #667085;
  display: block;
  font-size: 11px;
  line-height: 16px;
  margin: 8px 0;
}

.mc-fuel-insight__answers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mc-fuel-insight__answer {
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 8px;
  color: #475467;
  cursor: pointer;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  padding: 12px;
  position: relative;
  text-align: left;
  width: 100%;
}

.mc-fuel-insight__answer::after {
  background-image: url('~@/assets/images/dashboard/card.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  top: 0;
  content: '';
  height: 99px;
  pointer-events: none;
  position: absolute;
  right: 0;
  width: 134px;
  z-index: 0;
}

.mc-fuel-insight__answer > * {
  position: relative;
  z-index: 1;
}

.mc-fuel-insight__answer:hover,
.mc-fuel-insight__answer:focus {
  border-color: #98a2b3;
  outline: none;
}

.mc-fuel-insight__answer-heading {
  align-items: flex-start;
  display: flex;
  font-weight: 500;
  gap: 8px;
  justify-content: space-between;
}

.mc-fuel-insight__answer-heading i {
  color: #2076da;
  flex: 0 0 auto;
  font-size: 14px;
}

.mc-fuel-insight__answer-content {
  display: block;
  font-weight: 400;
  margin-top: 16px;
  white-space: pre-line;
}
</style>
